/**
 * Лимит тарифа исчерпан: стеклянная модалка с тем, что именно закончилось,
 * и платными тарифами («Практика» и «Максимум»). На телефоне тарифы —
 * карусель со свайпом, открытая на том, что снимает ограничение, — модалка
 * не вытягивается в длинную ленту; на широком экране — две карточки рядом.
 * Упёрся в дневной лимит уроков — можно посмотреть рекламу за +1 урок.
 * Портал в body — поверх шапки и нижнего меню; Esc и клик мимо закрывают.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { TFunction } from 'i18next';

import { PLAN_PRICES, adBonusFor, type LimitReachedDetails, type PaidPlan } from '@aptile/shared';

import { usePaywallStore } from '../../store/paywallStore';
import { AdBonusButton } from './AdBonusButton';
import { PlanCarousel } from './PlanCarousel';
import { planFeatures, usedUpKey } from './planText';
import { Button } from '../Button';
import { Quokka } from '../Quokka';
import { IconCheck, IconX } from '../icons';

/** Что закончилось — фразой: «Ты использовал все 150 сообщений с Тико в этом месяце». */
function usedUp(t: TFunction, limit: LimitReachedDetails): string {
  return t(usedUpKey(limit.metric, limit.window), { count: limit.limit });
}

const PAID_PLANS: readonly PaidPlan[] = ['PRACTICE', 'MAX'];

/** Компактная карточка тарифа в модалке: название, цена, что входит. */
function PaywallPlan({ plan, recommended }: { readonly plan: PaidPlan; readonly recommended: boolean }) {
  const { t } = useTranslation();
  const dark = plan === 'MAX';
  return (
    <article
      data-testid={`paywall-plan-${plan}`}
      className={[
        'relative flex h-full flex-col gap-2 rounded-2xl border p-4 text-left',
        dark ? 'border-transparent text-[#F7F4ED]' : 'border-line bg-surface/80 text-heading',
        recommended ? 'ring-2 ring-accent/70' : '',
      ].join(' ')}
      style={dark ? { background: 'linear-gradient(140deg, #6E5646 0%, #4A3B31 60%, #3A2E26 100%)' } : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-heading text-lg font-extrabold">{t(`plans.name.${plan}`)}</p>
        {recommended && <span className="shrink-0 rounded-full bg-accent px-2 py-0.5 font-heading text-xs font-extrabold text-on-accent">{t('paywall.recommended')}</span>}
      </div>
      <p className="flex items-baseline gap-1">
        <span className="font-heading text-2xl font-black sm:text-3xl">{PLAN_PRICES[plan].month} ₽</span>
        <span className={dark ? 'text-[#F7F4ED]/70' : 'text-muted'}>{t('plans.perMonth')}</span>
      </p>
      <ul className="flex flex-col gap-1 sm:gap-1.5">
        {planFeatures(t, plan, { common: false }).map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-[13px] leading-snug sm:text-sm">
            <IconCheck size={16} aria-hidden="true" className={`mt-0.5 shrink-0 ${dark ? 'text-[#E8B87A]' : 'text-correct'}`} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

export function PaywallModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const limit = usePaywallStore((state) => state.limit);
  const close = usePaywallStore((state) => state.close);

  useEffect(() => {
    if (limit === null) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [limit, close]);

  // Какой бонус за рекламу снимает этот лимит; ступень рекламой не открыть.
  const bonus = limit === null ? null : adBonusFor(limit.metric);

  return createPortal(
    <AnimatePresence>
      {limit !== null && (
        <motion.div
          key="paywall"
          className="fixed inset-0 z-[100] grid place-items-center overflow-y-auto bg-heading/30 p-2 backdrop-blur-sm sm:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={t('paywall.title')}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 360, damping: 30 }}
            className="glass relative my-auto flex w-full max-w-md flex-col items-center gap-3 overflow-hidden rounded-card border border-white/40 bg-surface/85 p-5 text-center shadow-card-lg backdrop-blur-2xl sm:gap-4 sm:p-6 lg:max-w-2xl lg:p-8"
          >
            <button
              type="button"
              onClick={close}
              aria-label={t('toast.close')}
              className="tap-target absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full text-muted transition hover:bg-heading/10 hover:text-heading"
            >
              <IconX size={18} aria-hidden="true" />
            </button>
            {/* На телефоне без квокки: модалка должна помещаться в экран без прокрутки. */}
            <span className="max-sm:hidden">
              <Quokka mood="thinking" alt="" size="md" />
            </span>
            <h2 className="t-h1 max-sm:pr-8">{t('paywall.title')}</h2>
            <p className="text-body text-heading">{usedUp(t, limit)}</p>
            {/* На телефоне рекомендацию показывает метка на карточке тарифа. */}
            <p className="text-muted max-sm:hidden">
              {t(`paywall.upgrade.${limit.upgradeTo}`, { price: PLAN_PRICES[limit.upgradeTo].month })}
            </p>
            {/* Тарифы: на телефоне — свайп, на широком экране — рядом. */}
            <div className="w-full">
              <PlanCarousel
                label={t('plans.title')}
                labels={PAID_PLANS.map((plan) => t(`plans.name.${plan}`))}
                initial={PAID_PLANS.indexOf(limit.upgradeTo)}
                desktopColumns={2}
                bleed="modal"
              >
                {PAID_PLANS.map((plan) => (
                  <PaywallPlan key={plan} plan={plan} recommended={plan === limit.upgradeTo} />
                ))}
              </PlanCarousel>
              <p className="mt-2 text-xs text-muted sm:text-sm">{t('paywall.common')}</p>
            </div>
            <div className="flex w-full flex-col gap-2">
              <Button
                fullWidth
                autoFocus
                onClick={() => {
                  close();
                  navigate('/plans');
                }}
              >
                {t('paywall.seePlans')}
              </Button>
              {/* Лимит уроков, разговора с Тико или игр — можно не платить, а посмотреть рекламу за бонус. */}
              {bonus !== null && limit.metric !== 'LEVEL' && <AdBonusButton bonusType={bonus} metric={limit.metric} />}
              <button type="button" onClick={close} className="min-h-11 rounded-button px-5 py-2 font-heading font-bold text-muted transition hover:bg-heading/5 hover:text-heading sm:py-3">
                {t('paywall.later')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
